import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import * as qs from 'query-string';
import { IBaseResponse, IRecipientProfileHeaderInfo } from '@nkr/models/index';
import { HistogeneticsEntityType, RecipientAction } from '@nkr/enums/index';
import { AppActionCreators, ExpertSystemActionCreators, IApplicationState, IAppState, IRecipientHeaderInfoResponse, IRecipientLookupResponse, RecipientActionCreators } from '@nkr/store/index';
import { AfterAction, IRecipientProfileContext, RecipientProfileProvider, ToggleType, editNavKey, tabList } from './models';
import { ILookupData } from './lookup-data';
import { toast } from 'react-toastify';
import General from './general';
import ActivityLog from './activity-log';
import ContactInformation from './contact-info';
import MedicalDetails from './medical-details/index';
import HlaInformation from './hla-info';
import Records from './records';
import FinancialIformation from './financial-information/index';
import PostTransplant from './post-transplant';
import CheekSwabRequest, { CheekSwabType } from '@nkr/views/cheek-swab-request-form';
import RecipientProfileHeaderInfo from './profile-header';
import RecipientNav from '../recipient-nav';
import DataChangedWarning from '@nkr/views/recipients/data-changed-warning';
import RecipientProfileSaveRow from './recipient-save-row';
import PairedDonors from './paired-donors';
import AntibodyScreening from './antibody-screening';
import ConfirmDeleteModal from '../confirm-delete-modal';
import EmailRecipientDetailsModal from './email-recipient-details-modal';
import DonorDeclines from './donor-declines';
import { downloadFile } from '@nkr/services/download-file';
import { TabWrapper } from './styles';
import SubHeader from './sub-header';

interface IProps {
    centerId: number,
    recipientId: number,
    section: string,
    onUpdateRecipientAlias: (alias: string) => void
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getHeaderInfo: (centerId: number, recipientId: number) => Promise<IRecipientHeaderInfoResponse>,
    getRecipientLookupData: (centerId: number, recipientId: number) => Promise<IRecipientLookupResponse>,
    deleteRecipient: (recipientId: number, reasonCode: number, reason: string, initials: string) => Promise<IBaseResponse>,
    printDetails: (centerId: number, recipientId: number) => Promise<any>
    emailDetails: (centerId: number, recipientId: number, contactId: number) => Promise<IBaseResponse>,
    printRequisition: (orderId: number, entityTypeId: number) => Promise<any>
}

type TProps = IProps & IStateProps & IDispatchProps;

const RecipientProfile = (props: TProps) => {
    const [headerInfo, setHeaderInfo] = useState<IRecipientProfileHeaderInfo>(null);
    const [toggleType, setToggleType] = useState(ToggleType.NoMessage);
    const [messages, setMessages] = useState([]);
    const [showCheekOrderNotification, setShowCheekOrderNotification] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [allowSaveWithNoChanges, setAllowSaveWithNoChanges] = useState(false);
    const [dataChangedWarning, setDataChangedWarning] = useState({ show: false, title: '', href: '' });
    const [cheekSwabModal, setCheekSwabModal] = useState({ show: false, type: null });
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [askForEmailAddress, setAskForEmailAddress] = useState(false);


    const [lookupData, setLookupData] = useState<ILookupData>({
        stateList: [],
        voucherStatusList: [],
        coordinatorList: [],
        recipientInactiveReasonList: [],
        compatiblePairKpdReasonList: [],
        deleteReasonList: [],
        insuranceGroupList: [],
        hlaValueList: [],
        raceList: [],
        antiATiterValueList: [],
        contactList: []
    });

    const saveRef = useRef(null);
    const afterRef = useRef(null);

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {

        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''), { arrayFormat: 'bracket' });
        let swabRequestAction = parsed.reqType != null ? parseInt(parsed.reqType) : '';

        Promise.all([
            props.getHeaderInfo(props.centerId, props.recipientId),
            props.getRecipientLookupData(props.centerId, props.recipientId)
        ])
            .then(([headerResponse, lookupDataResponse]) => {
                if (headerResponse.data.success && lookupDataResponse.data.success) {
                    setHeaderInfo(headerResponse.data.results);
                    setLookupData(lookupDataResponse.data.results);

                    props.onUpdateRecipientAlias(headerResponse.data.results.alias);

                    if (swabRequestAction != null && swabRequestAction != '') {
                        let id = 0;
                        const action =
                            swabRequestAction == RecipientAction.ReprintRequisition
                                ? RecipientAction.ReprintRequisition
                                : RecipientAction.UtilizeBulkCheekSwab;

                        handleCheekSwabRequest(id, action);
                    }
                }
            })
    };

    const updateHeader = () => {
        props.getHeaderInfo(props.centerId, props.recipientId).then(response => {
            if (response.data.success) {
                setHeaderInfo(response.data.results);
            }
        });
    };

    const handleCheekSwabRequest = (id: number, action: RecipientAction) => {
        let type = 0;

        if (action == RecipientAction.Order3gTissueTyping) {
            type = CheekSwabType.Order3gTissueTyping;
        }

        if (action == RecipientAction.UtilizeBulkCheekSwab) {
            type = CheekSwabType.UtilizeBulkCheekSwab;
        }

        setCheekSwabModal({
            show: true,
            type
        });
    };


    const handleItemSelected = (id: number, action: RecipientAction) => {
        switch (action) {
            case RecipientAction.Order3gTissueTyping: handleCheekSwabRequest(id, action); break;
            case RecipientAction.UtilizeBulkCheekSwab: handleCheekSwabRequest(id, action); break;
            case RecipientAction.Delete: setConfirmDelete(true); break;
            case RecipientAction.ReprintRequisition: handleReprintRequisition(id, HistogeneticsEntityType.Recipient); break;
        }
    }

    const handleCancelCheekSwabModal = async (cancel: boolean) => {
        setCheekSwabModal({
            show: false,
            type: null
        });

        if (!cancel) {
            let response = await props.getHeaderInfo(props.centerId, props.recipientId);

            if (response.data.success) {
                setHeaderInfo(response.data.results);
            }
        }
    };

    const handleDelete = (reasonCode: number, reason: string, initials: string) => {
        setConfirmDelete(false);

        props.showLoading('Deleting recipient, one moment...').then(() => {
            props.deleteRecipient(props.recipientId, reasonCode, reason, initials)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Recipient deleted succesfully, you will be redirected to the list page', {
                            onClose: () => {
                                window.location.href = '/center/' + props.centerId + '/recipients';
                            }
                        })
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleCancelConfirmDelete = () => {
        setConfirmDelete(false);
    }

    const handleUpdateAfterLoad = (headerInfo: IRecipientProfileHeaderInfo) => {
        setHeaderInfo(headerInfo);
    };

    const handleUpdateHasChanges = (value: boolean) => {
        setHasChanges(value);
    };

    const handleUpdateAllowSaveWithNoChanges = (value: boolean) => {
        setAllowSaveWithNoChanges(value);
    };

    const handleUpdateDataChangedWarning = (show: boolean, title: string, href: string) => {
        setDataChangedWarning({
            show,
            title,
            href
        });
    };

    const handleUpdateErrors = (toggleType: ToggleType, messages: string[]) => {
        setToggleType(toggleType);
        setMessages(messages);
    };

    const handleCheekOrderNotify = (show: boolean) => {
        setShowCheekOrderNotification(show);
    };

    const handleTabClick = (href: string) => {
        let tab = tabList.find(x => x.value == props.section);

        if (hasChanges) {
            setDataChangedWarning({
                show: true,
                title: tab != null ? tab.name : '',
                href
            });
        } else {
            window.location.href = href;
        }
    };

    const handleDiscardChanges = () => {
        let href = dataChangedWarning.href;

        setDataChangedWarning({
            show: false,
            title: '',
            href: ''
        });

        window.location.href = href;
    };

    const handleCancelWarning = () => {
        setDataChangedWarning({
            show: false,
            title: '',
            href: ''
        });
    };

    const handleRegisterSave = (saveRef: MutableRefObject<any>, save: any) => {
        saveRef.current = save;
    };

    const handleSave = (afterAction: AfterAction) => {
        if (saveRef.current != null) {
            setToggleType(ToggleType.NoMessage);
            setMessages([]);
            saveRef.current(afterAction);
        }

        afterRef.current = afterAction;
    };

    const handleAfterAction = (afterAction: AfterAction) => {
        if (afterAction == AfterAction.Print) {
            props.showLoading('Downloading recipient details, one moment please...').then(() => {
                props.printDetails(props.centerId, props.recipientId).then((response: any) => {
                    const blob = new Blob([response.data], { type: 'text/html' });
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    props.hideLoading();
                });
            });
        }

        if (afterAction == AfterAction.Email) {
            setAskForEmailAddress(true);
        }

        if (afterAction == AfterAction.AddDonor) {
            window.location.href = `/center/${props.centerId}/donor/add?recipientId=${props.recipientId}`;
        }

        if (afterAction == null) {
            updateHeader();
        }
    };

    const handleEmailDetails = (contactId: number) => {
        setAskForEmailAddress(false);

        let contact = lookupData.contactList.find(x => x.id == contactId);

        props.showLoading('Emailing recipient details, one moment please...').then(() => {
            props.emailDetails(props.centerId, props.recipientId, contactId)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Successfully emailed recipient details to ' + contact.fullName);
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleReprintRequisition = (requsitionId: number, entityTypeId: number) => {
        props.showLoading('Downloading requisition file, one moment please...').then(() => {
            props.printRequisition(requsitionId, entityTypeId).then((response: any) => {
                downloadFile(response.data, `requisition-file.pdf`, 'application/pdf');

                props.hideLoading();
            });
        });
    }

    let contextValue: IRecipientProfileContext = {
        activeKey: props.section,
        isAdmin: props.app.user.group.isSuper,
        centerId: props.centerId,
        recipientId: props.recipientId,
        headerInfo,
        toggleType,
        messages,
        showCheekOrderNotification,
        hasChanges,
        allowSaveWithNoChanges,
        dataChangedWarning,
        updateAfterLoad: handleUpdateAfterLoad,
        updateErrors: handleUpdateErrors,
        updateHasChanges: handleUpdateHasChanges,
        updateAllowSaveWithNoChanges: handleUpdateAllowSaveWithNoChanges,
        updateDataChangedWarning: handleUpdateDataChangedWarning,
        refreshHeader: getData,
        onCheekSwabRequest: () => { },
        onCheekOrderNotify: handleCheekOrderNotify,
        registerSave: handleRegisterSave,
        onItemSelected: handleItemSelected,
        doAfterAction: handleAfterAction
    }

    let alias = headerInfo != null ? headerInfo.alias : '';

    return (
        <>
            <RecipientProfileProvider value={contextValue}>
                <div style={{ padding: '0px 10px' }}>
                    <RecipientNav
                        activeKey={'RECIPIENT-PROFILE'}
                        isAdmin={false}
                        isNkrLogin={props.app.user.group.isSuper}
                        centerId={props.centerId}
                        recipientId={props.recipientId}
                        recipientAlias={alias}
                    />

                    <RecipientProfileHeaderInfo
                        onOrderTypingKit={() => handleCheekSwabRequest(props.recipientId, RecipientAction.Order3gTissueTyping)} />

                    <RecipientProfileSaveRow
                        showButtons={true}
                        onTabClick={handleTabClick}
                        onSave={handleSave} />

                    <TabWrapper style={{ padding: '0px 0px 10px 0px' }}>
                        {headerInfo && <SubHeader headerInfo={headerInfo} /> }
                        
                        {props.section == editNavKey.GENERAL && (
                            <General saveRef={saveRef} lookupData={lookupData} />
                        )}

                        {props.section == editNavKey.CONTACT_INFORMATION && (
                            <ContactInformation saveRef={saveRef} lookupData={lookupData} />
                        )}

                        {props.section == editNavKey.MEDICAL_DETAILS && (
                            <MedicalDetails saveRef={saveRef} lookupData={lookupData} />
                        )}

                        {props.section == editNavKey.HLA_INFORMATION && (
                            <HlaInformation saveRef={saveRef} afterRef={afterRef} lookupData={lookupData} />
                        )}

                        {props.section == editNavKey.RECORDS && (
                            <Records saveRef={saveRef} />
                        )}

                        {props.section == editNavKey.FINANCIAL_INFORMATION && (
                            <FinancialIformation saveRef={saveRef} lookupData={lookupData} />
                        )}

                        {props.section == editNavKey.ACTIVITY_LOG && (
                            <ActivityLog saveRef={saveRef} alias={alias} />
                        )}

                        {props.section == editNavKey.DONOR_DECLINES && (
                            <DonorDeclines saveRef={saveRef} />
                        )}

                        {props.section == editNavKey.POST_TRANSPLANT && (
                            <PostTransplant saveRef={saveRef} />
                        )}

                        {props.section == editNavKey.PAIRED_DONORS && (
                            <PairedDonors saveRef={saveRef} />
                        )}

                        {props.section == editNavKey.ANTIBODY_SCREENINGS && (
                            <AntibodyScreening saveRef={saveRef} />
                        )}
                    </TabWrapper>
                </div>
            </RecipientProfileProvider>

            <CheekSwabRequest
                centerId={props.centerId}
                donorId={0}
                recipientId={props.recipientId}
                isModal={true}
                show={cheekSwabModal.show}
                type={cheekSwabModal.type}
                onFinished={handleCancelCheekSwabModal} />

            <DataChangedWarning
                show={dataChangedWarning.show}
                title={dataChangedWarning.title}
                onDiscard={handleDiscardChanges}
                onCancel={handleCancelWarning}
            />

            <ConfirmDeleteModal
                show={confirmDelete}
                alias={alias}
                reasonList={lookupData.deleteReasonList}
                onSubmit={handleDelete}
                onCancel={handleCancelConfirmDelete}
            />

            <EmailRecipientDetailsModal
                show={askForEmailAddress}
                alias={alias}
                contactList={lookupData.contactList}
                onSubmit={handleEmailDetails}
                onCancel={() => setAskForEmailAddress(false)}
            />
        </>
    );
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getHeaderInfo: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getHeaderinfo(centerId, recipientId));
        },
        getRecipientLookupData: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getRecipientProfileLookupData(centerId, recipientId));
        },
        deleteRecipient: (recipientId, reasonCode, reason, initials) => {
            return dispatch(RecipientActionCreators.deleteRecipient(recipientId, reasonCode, reason, initials));
        },
        printDetails: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.printDetails(centerId, recipientId));
        },
        emailDetails: (centerId, recipientId, contactId) => {
            return dispatch(RecipientActionCreators.emailDetails(centerId, recipientId, contactId));
        },
        printRequisition: (orderId, entityType) => {
            return dispatch(ExpertSystemActionCreators.printRequisition(orderId, entityType));
        },
    }
}

export default connect<IStateProps, {}, IProps>(
    mapStateToProps,
    mapDispatchToProps)(RecipientProfile);

